import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { isAxiosError } from 'axios';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import isProd from '@utils/isProd';

import {
  SharepointActiveSheetChangedBroadcastAction,
  SharepointExcelPrefetchInterceptBroadcastAction,
} from 'constants/BackgroundScript';
import { isSharepointExcelWorkbookTabUrl } from 'constants/SharepointTrustedWorkbookUrls';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { getSharepointSheetTabTitlesForHostTab } from 'lib/api/getSharepointSheetTabTitles';
import {
  getSharepointWorkbookEwaContext,
  worksheetNameFromEwaContext,
} from 'lib/api/getSharepointWorkbookEwaContext';
import { getSharepointWorkbookRows } from 'lib/api/getSharepointWorkbookRows';
import { getCurrentTab } from 'lib/chromeScripting/util';
import { Maybe } from 'types/UtilityTypes';

import {
  logSharepointWorkbookRowsDev,
  logSharepointWorksheetSelectionDev,
} from '../lib/sharepointDebugLog';
import { useSharepointLoadBuildingProcessing } from '../providers/SharepointLoadBuildingProcessingProvider';
import type { SharepointLoadBuildingGraphState } from '../providers/context';
import type { SharepointWorkbookRowsResponse } from '../types/SharepointWorkbookRows';
import { sharepointGraphApiAllowedForDocument } from '../utils/sharepointLoadBuildingDataSourceFromUrl';
import { sharepointWorkbookRowsResponseSignature } from '../utils/sharepointWorkbookRowsResponseSignature';
import {
  type WorksheetCacheEntry,
  type WorksheetFailureCacheEntry,
  cacheEntryIsFresh,
  clearWorksheetCacheForDocument,
  failureCacheEntryIsFresh,
  worksheetCacheKey,
} from '../utils/worksheetCache';

const POLL_INTERVAL_MS = 12_000;
const PREFETCH_INTERCEPT_DEBOUNCE_MS = 450;
const PREFETCH_TAIL_TABS = 10;
const PREFETCH_CONCURRENCY = 2;
const HTTP_STATUS_CONTENT_TOO_LARGE = 413;

function isOversizedWorksheetError(error: unknown): boolean {
  return (
    isAxiosError(error) &&
    error.response?.status === HTTP_STATUS_CONTENT_TOO_LARGE
  );
}

function hostTabMatchesBroadcast(
  hostTabId: number | undefined,
  broadcastTabId: number | undefined
): boolean {
  return (
    broadcastTabId != null &&
    (hostTabId == null || broadcastTabId === hostTabId)
  );
}

function graphMetaFromRows(
  data: SharepointWorkbookRowsResponse
): NonNullable<SharepointLoadBuildingGraphState['graphMeta']> {
  return {
    fileName: data.fileName,
    worksheetName: data.worksheetName,
    rowCount: data.rowCount,
    columnCount: data.columnCount,
  };
}

type RuntimeBroadcastMessage = {
  action?: string;
  tabId?: number;
  documentUrl?: string;
  activeSheetName?: string;
  data?: unknown;
};

export type UseSharepointWorkbookGraphOptions = {
  enabled: boolean;
};

export function useSharepointWorkbookGraph({
  enabled,
}: UseSharepointWorkbookGraphOptions): SharepointLoadBuildingGraphState {
  const { beginGraphFetch, endGraphFetch } =
    useSharepointLoadBuildingProcessing();
  const {
    currentState: { tabId: sidebarTabId },
  } = useContext(SidebarStateContext);

  const [workbookRowsResponse, setWorkbookRowsResponse] =
    useState<Maybe<SharepointWorkbookRowsResponse>>(null);

  const graphMeta = useMemo(
    () =>
      workbookRowsResponse ? graphMetaFromRows(workbookRowsResponse) : null,
    [workbookRowsResponse]
  );

  const lastDocUrlRef = useRef('');
  const lastHostTabIdRef = useRef<number | undefined>(undefined);
  const lastKnownWorksheetRef = useRef<string | undefined>(undefined);
  const fetchGenerationRef = useRef(0);
  const worksheetCacheRef = useRef<Map<string, WorksheetCacheEntry>>(new Map());
  const worksheetFailureCacheRef = useRef<
    Map<string, WorksheetFailureCacheEntry>
  >(new Map());
  const prefetchInterceptDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const didInitialTailWarmForDocUrlRef = useRef('');
  const didInitialUserVisibleFetchForDocUrlRef = useRef('');
  const lastSkipDebugKeyRef = useRef('');
  const lastWorkbookLogSigRef = useRef('');
  const lastAppliedWorkbookRowsSigRef = useRef('');

  const isStaleFetch = (gen: number) => gen !== fetchGenerationRef.current;

  const clearRows = useCallback(() => {
    lastAppliedWorkbookRowsSigRef.current = '';
    setWorkbookRowsResponse(null);
  }, []);

  const applyRowsResponse = useCallback(
    (
      documentUrl: string,
      data: SharepointWorkbookRowsResponse,
      hostTabId: number | undefined,
      worksheetForLog: string | undefined
    ) => {
      const sig = sharepointWorkbookRowsResponseSignature(
        documentUrl,
        worksheetForLog,
        data
      );
      const wn = data.worksheetName?.trim();
      if (wn) {
        lastKnownWorksheetRef.current = wn;
      }
      if (lastAppliedWorkbookRowsSigRef.current === sig) {
        return;
      }
      lastAppliedWorkbookRowsSigRef.current = sig;
      if (!isProd() && lastWorkbookLogSigRef.current !== sig) {
        lastWorkbookLogSigRef.current = sig;
        logSharepointWorkbookRowsDev(documentUrl, data, {
          hostTabId,
          worksheetQueryParam: worksheetForLog,
        });
      }
      setWorkbookRowsResponse(data);
    },
    []
  );

  const prefetchTailWorksheets = useCallback(
    async (
      documentUrl: string,
      hostTabId: number | undefined,
      currentWorksheet: string | undefined
    ) => {
      if (
        !sharepointGraphApiAllowedForDocument(documentUrl) ||
        hostTabId == null
      ) {
        return;
      }

      const titles = await getSharepointSheetTabTitlesForHostTab(hostTabId);
      if (titles.length === 0) {
        return;
      }

      const cur = currentWorksheet?.trim();
      const neighborNames: string[] = [];
      if (cur) {
        const idx = titles.findIndex((t) => t === cur);
        if (idx >= 0) {
          for (
            let j = Math.max(0, idx - 4);
            j <= Math.min(titles.length - 1, idx + 4);
            j += 1
          ) {
            const t = titles[j];
            if (t && t !== cur) {
              neighborNames.push(t);
            }
          }
        }
      }

      const candidates = [
        ...new Set([...neighborNames, ...titles.slice(-PREFETCH_TAIL_TABS)]),
      ].filter((t) => t && t !== currentWorksheet);

      for (let i = 0; i < candidates.length; i += PREFETCH_CONCURRENCY) {
        const batch = candidates.slice(i, i + PREFETCH_CONCURRENCY);
        await Promise.all(
          batch.map(async (ws) => {
            const key = worksheetCacheKey(documentUrl, ws);
            const existing = worksheetCacheRef.current.get(key);
            if (existing && cacheEntryIsFresh(existing)) {
              return;
            }
            const failed = worksheetFailureCacheRef.current.get(key);
            if (failed && failureCacheEntryIsFresh(failed)) {
              return;
            }
            try {
              const data = await getSharepointWorkbookRows({
                url: documentUrl,
                worksheet: ws,
              });
              worksheetCacheRef.current.set(key, {
                body: data,
                at: Date.now(),
              });
            } catch (error: unknown) {
              if (isOversizedWorksheetError(error)) {
                worksheetFailureCacheRef.current.set(key, {
                  at: Date.now(),
                });
              }
              /* best-effort warm */
            }
          })
        );
      }
    },
    []
  );

  const resolveWorksheetName = useCallback(
    async (
      hostTabId: number | undefined,
      worksheetOverride: string | undefined,
      gen: number
    ): Promise<{
      worksheet: string | undefined;
      ewa: Awaited<ReturnType<typeof getSharepointWorkbookEwaContext>>;
    }> => {
      const override = worksheetOverride?.trim();
      if (override) {
        return { worksheet: override, ewa: null };
      }
      if (hostTabId != null) {
        const ewa = await getSharepointWorkbookEwaContext(hostTabId);
        if (isStaleFetch(gen)) {
          return { worksheet: undefined, ewa: null };
        }
        const fromEwa = worksheetNameFromEwaContext(ewa)?.trim();
        if (fromEwa) {
          return { worksheet: fromEwa, ewa };
        }
      }
      const fallback = lastKnownWorksheetRef.current?.trim();
      return { worksheet: fallback || undefined, ewa: null };
    },
    []
  );

  const fetchRowsForUrl = useCallback(
    async (
      documentUrl: string,
      hostTabId?: number,
      worksheetOverride?: string,
      opts?: {
        bypassWorksheetCache?: boolean;
        showProcessingIndicator?: boolean;
      }
    ) => {
      lastHostTabIdRef.current = hostTabId;
      lastDocUrlRef.current = documentUrl;

      if (!sharepointGraphApiAllowedForDocument(documentUrl)) {
        clearRows();
        return;
      }

      const gen = (fetchGenerationRef.current += 1);
      const showProcessingIndicator = opts?.showProcessingIndicator === true;
      let requestedWorksheet: string | undefined;

      if (showProcessingIndicator) {
        beginGraphFetch();
      }
      try {
        const { worksheet, ewa } = await resolveWorksheetName(
          hostTabId,
          worksheetOverride,
          gen
        );
        if (isStaleFetch(gen) || !worksheet?.trim()) {
          return;
        }
        requestedWorksheet = worksheet;

        if (!isProd()) {
          logSharepointWorksheetSelectionDev({
            hostTabId,
            documentUrl,
            ewaFromServiceWorker: ewa,
            worksheetForApi: worksheet,
          });
        }

        const cacheKey = worksheetCacheKey(documentUrl, worksheet);
        const cached = worksheetCacheRef.current.get(cacheKey);
        const failed = worksheetFailureCacheRef.current.get(cacheKey);
        if (failed && failureCacheEntryIsFresh(failed)) {
          clearRows();
          return;
        }
        if (
          !opts?.bypassWorksheetCache &&
          cached &&
          cacheEntryIsFresh(cached)
        ) {
          if (isStaleFetch(gen)) {
            return;
          }
          applyRowsResponse(documentUrl, cached.body, hostTabId, worksheet);
          void prefetchTailWorksheets(documentUrl, hostTabId, worksheet);
          return;
        }

        const data = await getSharepointWorkbookRows({
          url: documentUrl,
          worksheet,
        });
        if (isStaleFetch(gen)) {
          return;
        }

        worksheetCacheRef.current.set(cacheKey, { body: data, at: Date.now() });
        applyRowsResponse(documentUrl, data, hostTabId, worksheet);

        void prefetchTailWorksheets(documentUrl, hostTabId, worksheet);
      } catch (e: unknown) {
        if (isStaleFetch(gen)) {
          return;
        }
        if (isOversizedWorksheetError(e)) {
          if (requestedWorksheet) {
            const cacheKey = worksheetCacheKey(documentUrl, requestedWorksheet);
            worksheetFailureCacheRef.current.set(cacheKey, {
              at: Date.now(),
            });
          }
        }
        if (!isProd()) {
          console.warn(
            '[Drumkit SharePoint] workbook-rows fetch failed',
            documentUrl,
            e
          );
        }
        clearRows();
      } finally {
        if (showProcessingIndicator) {
          endGraphFetch();
        }
      }
    },
    [
      applyRowsResponse,
      beginGraphFetch,
      clearRows,
      endGraphFetch,
      prefetchTailWorksheets,
      resolveWorksheetName,
    ]
  );

  const refreshGraphRowsBypassCache = useCallback(() => {
    const docUrl = lastDocUrlRef.current;
    const hostTabId = lastHostTabIdRef.current;
    if (
      !docUrl ||
      hostTabId == null ||
      !isSharepointExcelWorkbookTabUrl(docUrl) ||
      !sharepointGraphApiAllowedForDocument(docUrl)
    ) {
      return;
    }
    clearWorksheetCacheForDocument(worksheetCacheRef.current, docUrl);
    clearWorksheetCacheForDocument(worksheetFailureCacheRef.current, docUrl);
    void fetchRowsForUrl(docUrl, hostTabId, undefined, {
      bypassWorksheetCache: true,
      showProcessingIndicator: true,
    });
  }, [fetchRowsForUrl]);

  const resolveTrustedDocUrl = useCallback(
    async (hostTabId: number, urlHint?: string): Promise<string> => {
      for (const candidate of [urlHint?.trim(), lastDocUrlRef.current.trim()]) {
        if (candidate && isSharepointExcelWorkbookTabUrl(candidate)) {
          return candidate;
        }
      }
      if (typeof chrome !== 'undefined' && chrome.tabs?.get) {
        const tab = await chrome.tabs.get(hostTabId);
        const u = (tab?.url ?? '').trim();
        return isSharepointExcelWorkbookTabUrl(u) ? u : '';
      }
      return '';
    },
    []
  );

  useEffect(() => {
    if (
      !enabled ||
      typeof chrome === 'undefined' ||
      !chrome.runtime?.onMessage?.addListener
    ) {
      return;
    }

    const scheduleDebouncedInterceptFetch = (
      docUrl: string,
      hostTabId: number
    ) => {
      if (prefetchInterceptDebounceRef.current != null) {
        clearTimeout(prefetchInterceptDebounceRef.current);
      }
      prefetchInterceptDebounceRef.current = setTimeout(() => {
        prefetchInterceptDebounceRef.current = null;
        void fetchRowsForUrl(docUrl, hostTabId, undefined, {
          bypassWorksheetCache: true,
        });
      }, PREFETCH_INTERCEPT_DEBOUNCE_MS);
    };

    const onRuntimeMessage = (msg: RuntimeBroadcastMessage) => {
      if (!hostTabMatchesBroadcast(lastHostTabIdRef.current, msg.tabId)) {
        return;
      }

      if (
        msg.action === SharepointExcelPrefetchInterceptBroadcastAction &&
        msg.tabId != null
      ) {
        void (async () => {
          const docUrl = await resolveTrustedDocUrl(
            msg.tabId!,
            typeof msg.documentUrl === 'string' ? msg.documentUrl : undefined
          );
          if (!docUrl || !sharepointGraphApiAllowedForDocument(docUrl)) {
            return;
          }
          lastHostTabIdRef.current = msg.tabId!;
          lastDocUrlRef.current = docUrl;
          scheduleDebouncedInterceptFetch(docUrl, msg.tabId!);
        })();
        return;
      }

      if (
        msg.action !== SharepointActiveSheetChangedBroadcastAction ||
        msg.tabId == null
      ) {
        return;
      }

      const sheetName =
        typeof msg.activeSheetName === 'string'
          ? msg.activeSheetName.trim()
          : '';
      if (sheetName) {
        lastKnownWorksheetRef.current = sheetName;
      }

      void (async () => {
        const docUrl = await resolveTrustedDocUrl(msg.tabId!, undefined);
        if (!docUrl) {
          return;
        }
        lastHostTabIdRef.current = msg.tabId!;
        lastDocUrlRef.current = docUrl;
        void fetchRowsForUrl(docUrl, msg.tabId, sheetName || undefined, {
          showProcessingIndicator: true,
        });
      })();
    };

    chrome.runtime.onMessage.addListener(onRuntimeMessage);
    return () => {
      chrome.runtime.onMessage.removeListener(onRuntimeMessage);
      if (prefetchInterceptDebounceRef.current != null) {
        clearTimeout(prefetchInterceptDebounceRef.current);
        prefetchInterceptDebounceRef.current = null;
      }
    };
  }, [enabled, fetchRowsForUrl, resolveTrustedDocUrl]);

  useEffect(() => {
    if (!enabled) {
      clearRows();
      lastDocUrlRef.current = '';
      lastHostTabIdRef.current = undefined;
      lastKnownWorksheetRef.current = undefined;
      didInitialTailWarmForDocUrlRef.current = '';
      didInitialUserVisibleFetchForDocUrlRef.current = '';
      lastWorkbookLogSigRef.current = '';
      lastAppliedWorkbookRowsSigRef.current = '';
      lastSkipDebugKeyRef.current = '';
      worksheetCacheRef.current.clear();
      worksheetFailureCacheRef.current.clear();
      if (prefetchInterceptDebounceRef.current != null) {
        clearTimeout(prefetchInterceptDebounceRef.current);
        prefetchInterceptDebounceRef.current = null;
      }
      return;
    }

    if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
      return;
    }

    let cancelled = false;

    const logSkippedHostUrlOnce = (tabUrl: string) => {
      if (isProd()) {
        return;
      }
      const skipKey = tabUrl ? `untrusted:${tabUrl}` : 'no-host-url';
      if (lastSkipDebugKeyRef.current === skipKey) {
        return;
      }
      lastSkipDebugKeyRef.current = skipKey;
      if (!tabUrl) {
        console.debug(
          '[Drumkit SharePoint] workbook-rows skipped: no host tab URL (open Drumkit from the Excel/SharePoint tab).'
        );
      } else {
        console.debug(
          '[Drumkit SharePoint] workbook-rows skipped: URL not in trusted workbook list:',
          tabUrl
        );
      }
    };

    const onHostTabResolved = (
      tabUrl: string,
      hostTabId: number | undefined
    ) => {
      if (tabUrl !== lastDocUrlRef.current) {
        if (lastDocUrlRef.current) {
          worksheetCacheRef.current.clear();
          worksheetFailureCacheRef.current.clear();
          lastWorkbookLogSigRef.current = '';
          lastAppliedWorkbookRowsSigRef.current = '';
          lastKnownWorksheetRef.current = undefined;
        }
        lastDocUrlRef.current = tabUrl;
        didInitialTailWarmForDocUrlRef.current = '';
        didInitialUserVisibleFetchForDocUrlRef.current = '';
        clearRows();
      }

      void (async () => {
        if (
          sharepointGraphApiAllowedForDocument(tabUrl) &&
          hostTabId != null &&
          didInitialTailWarmForDocUrlRef.current !== tabUrl
        ) {
          didInitialTailWarmForDocUrlRef.current = tabUrl;
          void prefetchTailWorksheets(
            tabUrl,
            hostTabId,
            lastKnownWorksheetRef.current
          );
        }

        const isInitialFetchForDoc =
          didInitialUserVisibleFetchForDocUrlRef.current !== tabUrl;
        if (isInitialFetchForDoc) {
          didInitialUserVisibleFetchForDocUrlRef.current = tabUrl;
        }

        void fetchRowsForUrl(tabUrl, hostTabId, undefined, {
          bypassWorksheetCache: isInitialFetchForDoc,
          showProcessingIndicator: isInitialFetchForDoc,
        });
      })();
    };

    const pollHostWorkbook = () => {
      void getCurrentTab({ preferSidepanelHostTab: true })
        .then((tab) => {
          if (cancelled) {
            return;
          }
          const tabUrl = tab?.url?.trim() ?? '';
          const hostTabId =
            typeof tab?.id === 'number'
              ? tab.id
              : sidebarTabId > 0
                ? sidebarTabId
                : undefined;

          if (!tabUrl || !isSharepointExcelWorkbookTabUrl(tabUrl)) {
            logSkippedHostUrlOnce(tabUrl);
            clearRows();
            lastDocUrlRef.current = '';
            return;
          }

          onHostTabResolved(tabUrl, hostTabId);
        })
        .catch(() => {
          if (!cancelled) {
            clearRows();
          }
        });
    };

    pollHostWorkbook();
    const intervalId = setInterval(pollHostWorkbook, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    enabled,
    sidebarTabId,
    clearRows,
    fetchRowsForUrl,
    prefetchTailWorksheets,
  ]);

  return useMemo(
    (): SharepointLoadBuildingGraphState => ({
      workbookRowsResponse,
      graphMeta,
      refreshGraphRowsBypassCache: enabled
        ? refreshGraphRowsBypassCache
        : undefined,
    }),
    [enabled, workbookRowsResponse, graphMeta, refreshGraphRowsBypassCache]
  );
}
